import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Lottie from 'react-lottie';
import { useParams } from 'react-router-dom';

import Loading from '../../Loading';
import { 
  Header, 
  Banner, 
  DescriptionGroup, 
  Dot, 
  InformationsGroup, 
  InformationsSubscription, 
  LottieContainer, 
  SubtitleContainer, 
  SubtitleGroup,
  TextLottie,
  TicketPriceGroup,
  TitleGroup 
} from './style';

import animationData from '../../../assets/animation/empty-box-animation.json';

import { t } from 'i18next';
import { ChannelContext, IGroup, ISubgroup } from '../../../context/channel';
import { storage } from '../../../utils/Storage';
import { subscribeApi } from '../../../api/SubscribeApi';
import BaseModal from '../../../components/shared/BaseModal';
import { SubscriptionForm } from '../../../components/whitelabel/Form';
import { ModalSuccess } from '../../../components/shared/ModalSuccess';
import TopMenu from '../../../components/whitelabel/topMenu/TopMenu';
import Button from '../../../components/shared/button/Button';
import { formatTicketValue } from '../../../utils/helper';
import colors from '../../../theme/colors';
import SectionTalks from '../../../components/whitelabel/sectionTalks/SectionTalks';
import { API_URL } from '../../../settings';
import Footer from '../../../components/whitelabel/footer/Footer';
import Page404 from '../../Page404/404';
import { ITalksProps } from '../../../interfaces/university/talk';


interface Params {
  channel: string;
  groupSlug: string;
}


const Group = () => {
  const {data, groups, user} = React.useContext(ChannelContext)
  const { channel, groupSlug } = useParams<Params>();

  const [searchString, setSearchString] = useState<string>('')
  const [groupOfPage, setGroupOfPage] = useState<IGroup>();
  const [screen, setScreen] = useState<"LOADING" | "NOT_FOUND" | "WHITE_LABEL">("LOADING");

  const [subgroups, setSubgroups] = useState<ISubgroup[]>([]);
  const [talks, setTalks] = useState<ITalksProps[]>([]);

  const [formModalVisible, setFormModalVisible] = useState<boolean>(false);

  const [userSubscribed, setUserSubscribed] = useState<string>();
  const [awaitPayment, setAwaitPayment] = useState<string>();
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  useEffect(() => {
    const selectedGroup = groups.find(group => group.slug === groupSlug);

    if(selectedGroup) {
      setGroupOfPage(selectedGroup);
      setSubgroups(selectedGroup.subgroups);
      setTalks(selectedGroup.talks);
      setScreen("WHITE_LABEL");
    }

  }, [groupSlug, groups]);
  
  useEffect(() => {
    if(!groupOfPage) return;
    
    setSubgroups(groupOfPage.subgroups);
    setTalks(groupOfPage.talks);

  }, [groupOfPage]);

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  }
  
  useEffect(() => {
    if(!groupOfPage) return;

    const storageAwaitPayment = storage.get(`await_payment_${groupOfPage.owner_username}_group_${groupOfPage.slug}`);
    const storageUserSubscribed = storage.get(`is_subscribed_${groupOfPage.owner_username}_group_${groupOfPage.slug}`);
    setUserSubscribed(storageUserSubscribed);
    setAwaitPayment(storageAwaitPayment);

    checkAwaitPayment();

    if (showSuccessModal) {
      setShowSuccessModal(true);
      setTimeout(() => {
        handleCloseModal();
      }, 3000); // 3000ms = 3 seconds
    }

  }, [groupOfPage, showSuccessModal]);


  const checkAwaitPayment = async () => {
    if(!groupOfPage) return;

    if(!userSubscribed && !awaitPayment && groupOfPage.ticket_price > 0 && user){
      const response = await subscribeApi.hasPendingPayment("group", groupOfPage.slug, groupOfPage.owner_username);
      if(response){
        storage.set(`await_payment_${groupOfPage.owner_username}_group_${groupOfPage.slug}`, user.email);
        setAwaitPayment(user.email);
      }
    }
    else if(!userSubscribed && awaitPayment){
      const response = await subscribeApi.guessUserInfo(awaitPayment, groupOfPage.owner_username, groupOfPage.slug, "group", );
      if(response){
        storage.set(`is_subscribed_${groupOfPage.owner_username}_group_${groupOfPage.slug}`,  awaitPayment);
        storage.remove(`await_payment_${groupOfPage.owner_username}_group_${groupOfPage.slug}`);
      }
    }
  }

  const defaultOptions = {
    loop: true,
    autoplay: true, 
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };
  
  if(screen === "LOADING") return <Loading />;
  
  return (
    groupOfPage ? (
      <div>
          <BaseModal
              footer={null} 
              title={t('form.subscribe')!} 
              setToggleModal={setFormModalVisible} 
              toggleModal={formModalVisible}
              onCancel={() => setFormModalVisible(false)}
            >
            <SubscriptionForm
            setToggleModal={setFormModalVisible} 
            is_public={groupOfPage.is_public}
            data={data} 
            slug={groupOfPage.slug!}
            userName={groupOfPage.owner_username!}  
            fields={groupOfPage?.form?.fields || []}
            type={"group"}
            ticket_price={groupOfPage.ticket_price}
            setSuccessModal={setShowSuccessModal}
          />
          </BaseModal>

          <ModalSuccess
            title={t('form.subscribe')!} 
            text={t('formSubscribe.success')} 
            setToggleModal={setShowSuccessModal} 
            toggleModal={showSuccessModal} 
          />

          <Helmet>
            <title>{`Webinars - ${groupOfPage.name}`}</title>
            <meta property="og:title" content={`Webinars - ${groupOfPage.name}`} />
            <meta property="og:description" content={groupOfPage.description}/>  
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={`Webinars - ${groupOfPage.name}`} />
            <meta property="og:image" content={data.images.logo} />
            <meta property="og:image:alt" content={groupOfPage.description} />
          </Helmet>

          <TopMenu
            trilhaConteudo
            transparent={false}
            logoLightMode={data.images.logo}
            searchString={searchString}
            setSearchString={setSearchString}
          />

          {groupOfPage.banner && <Banner src={groupOfPage.banner} alt={t('group.banner.alt', {groupName: groupOfPage.name})!}/>}

          <Header>
            <InformationsGroup>
              <TitleGroup>{groupOfPage.name}</TitleGroup>
              <SubtitleContainer>
                <SubtitleGroup>{t('count.subgrupo', {count: groupOfPage.subgroups.length})}</SubtitleGroup>
                <Dot></Dot>
                <SubtitleGroup>{t('count.talk', {count: groupOfPage.talks.length})}</SubtitleGroup>
              </SubtitleContainer>
              <DescriptionGroup>{groupOfPage.description}</DescriptionGroup>
            </InformationsGroup>
            
            <InformationsSubscription>
            {(!userSubscribed && !awaitPayment) && <Button
                onClick={() => {setFormModalVisible(true)}}
                size = 'medium'
                enable
                typographyType='subheading3'
                style={{borderRadius: '30px'}}
                customBackground = {data.config.colors?.colorPrimary  ? data.config.colors.colorPrimary : colors.secondary}
              >
                {t('subscribe').toUpperCase()}
              </Button>}
              {
              (groupOfPage.ticket_price && groupOfPage.ticket_price !== 0 && !userSubscribed && !awaitPayment) ? 
                (<TicketPriceGroup>{formatTicketValue(groupOfPage.ticket_price)}</TicketPriceGroup>) : ("")
              }
            </InformationsSubscription>
          </Header>

          {groupOfPage.is_public || userSubscribed ? (
            <>
              {
                (subgroups.length === 0 && talks.filter(t => t.is_public).length === 0) &&
                <LottieContainer>
                  <TextLottie>Não encontramos nenhum webinar</TextLottie>
                  <Lottie 
                    options={defaultOptions}
                    isStopped={false}
                    isPaused={false}
                  />
                </LottieContainer>
              }
      
              {subgroups.map((subgroup: ISubgroup) => ( 
                   subgroup.talks.filter(t => t.is_public).length > 0 && <SectionTalks
                      key={subgroup.slug}
                      color={data.config.colors.colorPrimary}
                      title={subgroup.name}
                      description={subgroup.description}
                      colorBackground={data.config.colors.colorSectionTalks ? data.config.colors.colorSectionTalks : colors.offWhite }
                      talks={subgroup.talks}
                      slug={subgroup.slug}
                      apiUrl={API_URL}
                      wrap={data.config.wrapTalks ? data.config.wrapTalks :false}
                      channel={channel}
                      relationed={false}
                      type="subgroup"
                      user_is_subscribed={userSubscribed !== undefined}
                      group_is_public={groupOfPage.is_public}
                    />
                ))}
    
              {talks.filter(t => t.is_public).length > 0 &&
                <SectionTalks
                  key={groupOfPage.id}
                  color={data.config.colors.colorPrimary}
                  title="Webinars"
                  colorBackground={data.config.colors.colorSectionTalks ? data.config.colors.colorSectionTalks : colors.offWhite }
                  talks={talks}
                  slug={groupOfPage.slug}
                  apiUrl={API_URL}
                  wrap={data.config.wrapTalks ? data.config.wrapTalks :false}
                  channel={channel}
                  relationed={true}
                  user_is_subscribed={userSubscribed !== null}
                />
              }
            </>
          ) : (
            <LottieContainer>
              <TextLottie>{t("group.private")}</TextLottie>
              <Lottie 
                options={defaultOptions}
                isStopped={false}
                isPaused={false}
              />
            </LottieContainer>
          )}

          <Footer
            companyName={data.name}
            image={data.footer.logo ? data.footer.logo : data.images.logo}
            color={data ? data.footer.color : colors.black}
            textColor={data ? data.footer.footerTextColor : colors.black}
            social={data.footer.social}
          />
        </div>
    ) : ( <Page404/> )
  );
}

export default Group;
